// src/components/SavePlaceModal.jsx
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useUser, API_BASE } from "../context/UserContext";
import { STATUS_LABEL } from "../constants";

const C = {
  primary:    "#655d54",
  primaryDim: "#595149",
  bg:         "#faf9f6",
  container:  "#f4f4f0",
  containerHigh: "#edeeea",
  onSurface:  "#2f3430",
  variant:    "#5c605c",
  outline:    "#8a8e8a",
  outlineFaint: "rgba(101,93,84,0.08)",
  error:      "#9e422c",
};
const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";

const STATUS_OPTIONS = [
  { value: "want_to_go",      emoji: "🔖", label: STATUS_LABEL.want_to_go },
  { value: "visited",         emoji: "✅", label: STATUS_LABEL.visited },
  { value: "want_revisit",    emoji: "❤️", label: STATUS_LABEL.want_revisit },
];

const VISITED_STATUSES = ["visited", "want_revisit"];
const FOLDER_COLORS = ["#655d54", "#E8593C", "#3B8BD4", "#BA7517", "#7F77DD", "#D4537E"];

export default function SavePlaceModal({ place, onSave, onClose, editMode = false, onDelete, isGuest = false }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const isMobile = window.innerWidth <= 768;
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(place?.folder_id || null);
  const [status, setStatus] = useState(place?.status || "want_to_go");
  const [rating, setRating] = useState(place?.rating || 0);
  const [memo, setMemo] = useState(place?.memo || "");
  const [instagramUrl, setInstagramUrl] = useState(place?.instagram_post_url || "");
  // Photos: { type: "existing", url } or { type: "new", file, blobUrl }
  const initPhotos = () => {
    // Support pre-loaded photo files from PhotoPlaceImport
    if (place?._initPhotos?.length) return place._initPhotos;
    const urls = place?.photo_urls?.length ? place.photo_urls : (place?.photo_url ? [place.photo_url] : []);
    return urls.map((url) => ({ type: "existing", url }));
  };
  const [photos, setPhotos] = useState(initPhotos);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderColor, setNewFolderColor] = useState("#655d54");
  const [creatingFolder, setCreatingFolder] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API_BASE}/folders/`)
      .then((res) => setFolders(res.data))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!VISITED_STATUSES.includes(status)) setRating(0);
  }, [status]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      const res = await axios.post(`${API_BASE}/folders/`, {
        name: newFolderName.trim(), color: newFolderColor,
      });
      setFolders((prev) => [...prev, res.data]);
      setSelectedFolderId(res.data.id);
      setNewFolderName(""); setShowNewFolder(false);
    } catch (e) { alert(t("curated.create_failed")); }
    finally { setCreatingFolder(false); }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API_BASE}/folders/${folderId}`);
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      if (selectedFolderId === folderId) setSelectedFolderId(null);
    } catch (e) { alert(t("place.save_failed")); }
  };

  const handleFolderColorChange = async (folderId, newColor) => {
    try {
      await axios.patch(`${API_BASE}/folders/${folderId}`, { color: newColor });
      setFolders((prev) => prev.map((f) => f.id === folderId ? { ...f, color: newColor } : f));
    } catch { alert(t("place.save_failed")); }
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadError("");
    const remaining = 5 - photos.length;
    const valid = files.filter((f) => {
      if (!ALLOWED_TYPES.includes(f.type)) {
        setUploadError(t("place.file_type_error"));
        return false;
      }
      if (f.size > MAX_FILE_SIZE) {
        setUploadError(t("place.file_size_error"));
        return false;
      }
      return true;
    });
    const toAdd = valid.slice(0, remaining);
    const newPhotos = toAdd.map((file) => ({
      type: "new", file, blobUrl: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemovePhoto = (idx) => {
    setPhotos((prev) => {
      const removed = prev[idx];
      if (removed?.type === "new" && removed.blobUrl) {
        URL.revokeObjectURL(removed.blobUrl);
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSave = async () => {
    if (!place) return;
    if (photos.length === 0) {
      setUploadError(t("place.photo_required") || "At least one photo is required.");
      return;
    }
    setSaving(true);
    setUploadError("");
    try {
      const finalUrls = [];

      // 1) Keep existing URLs
      for (const p of photos) {
        if (p.type === "existing") finalUrls.push(p.url);
      }

      // 2) Upload new files (skip for guests — use blob URLs)
      const newFiles = photos.filter((p) => p.type === "new");
      if (newFiles.length > 0) {
        if (isGuest) {
          newFiles.forEach((p) => finalUrls.push(p.blobUrl));
        } else {
          setUploading(true);
          const formData = new FormData();
          newFiles.forEach((p) => formData.append("files", p.file));
          try {
            const uploadRes = await axios.post(
              `${API_BASE}/upload/photos`,
              formData,
            );
            finalUrls.push(...uploadRes.data.urls);
          } catch (uploadErr) {
            const detail = uploadErr.response?.data?.detail || uploadErr.message || "Upload failed";
            setUploading(false);
            setUploadError(`${t("place.photo_upload_failed")}: ${detail}`);
            setSaving(false);
            return;
          }
          setUploading(false);
        }
      }

      // 3) Save place
      await onSave({
        ...place,
        folder_id: place?.folder_id || null,
        status: place?.status || "visited",
        rating: place?.rating || null,
        memo: memo.trim() || null,
        photo_url: finalUrls[0] || null,
        photo_urls: finalUrls.length > 0 ? finalUrls : null,
        instagram_post_url: place?.instagram_post_url || null,
      });
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Unknown error";
      alert(`${t("place.save_failed")}: ${detail}`);
    }
    finally { setSaving(false); }
  };

  if (!place) return null;

  return createPortal(
    <>
      {/* fonts loaded in App.css */}
      <div
        onClick={(e) => e.target === e.currentTarget && onClose()}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(47,52,48,0.3)",
          backdropFilter: "blur(4px)",
          display: "flex", alignItems: isMobile ? "flex-end" : "center",
          justifyContent: "center", zIndex: 100,
        }}
      >
        <div style={{
          background: C.bg,
          borderRadius: isMobile ? "20px 20px 0 0" : 20,
          width: "100%", maxWidth: 520,
          maxHeight: isMobile ? "90vh" : "85vh",
          overflowY: "auto",
          boxShadow: isMobile
            ? "0 -8px 40px rgba(47,52,48,0.12)"
            : "0 20px 60px rgba(47,52,48,0.18)",
          margin: isMobile ? 0 : 24,
        }}>
          {/* Handle */}
          <div style={{ display: "flex", justifyContent: "center", padding: "14px 0 6px" }}>
            <div style={{ width: 32, height: 3, background: C.outline, borderRadius: 2, opacity: 0.4 }} />
          </div>

          {/* Header */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            padding: "12px 24px 20px",
            borderBottom: `1px solid ${C.outlineFaint}`,
          }}>
            <div>
              <p style={{ margin: "0 0 4px", fontFamily: FL, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.15em", color: C.outline }}>
                {editMode ? t("place.edit_entry") : t("place.new_entry")}
              </p>
              <h3 style={{ margin: 0, fontFamily: FH, fontSize: 20, fontWeight: 700, color: C.onSurface }}>
                {place.name}
              </h3>
              {place.address && !editMode && (
                <p style={{ margin: "4px 0 0", fontFamily: FL, fontSize: 12, color: C.outline }}>{place.address}</p>
              )}
            </div>
            <button onClick={onClose} style={{
              background: "none", border: "none",
              fontSize: 20, color: C.outline, cursor: "pointer", padding: 4,
            }}>✕</button>
          </div>

          <div style={{ padding: "20px 24px", paddingBottom: isMobile ? "calc(64px + 24px)" : 24 }}>

            {/* Status, Rating, Collection — hidden for Phase 1 */}

            {/* Notes */}
            <Section label={t("place.notes")}>
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder={t("place.notes_placeholder")}
                maxLength={300}
                rows={3}
                style={{
                  width: "100%", padding: "12px 14px",
                  background: C.container, border: "none", borderRadius: 10,
                  fontFamily: FH, fontSize: 13, fontStyle: "italic",
                  color: C.onSurface, outline: "none",
                  boxSizing: "border-box", resize: "none",
                  lineHeight: 1.7,
                }}
                onFocus={(e) => e.target.style.background = "#ede0d5"}
                onBlur={(e) => e.target.style.background = C.container}
              />
            </Section>

            {/* Photos */}
            <Section label={`Photos (${photos.length}/5)`}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {photos.map((photo, idx) => {
                  const src = photo.type === "existing" ? photo.url : photo.blobUrl;
                  return (
                    <div key={idx} style={{ position: "relative", width: 72, height: 72, borderRadius: 10, overflow: "hidden" }}>
                      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button
                        onClick={() => handleRemovePhoto(idx)}
                        style={{
                          position: "absolute", top: 2, right: 2,
                          width: 20, height: 20, borderRadius: "50%",
                          background: "rgba(0,0,0,0.5)", color: "#fff",
                          border: "none", cursor: "pointer",
                          fontSize: 12, lineHeight: "20px", padding: 0,
                        }}
                      >&times;</button>
                    </div>
                  );
                })}
                {photos.length < 5 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: 72, height: 72, borderRadius: 10,
                      border: `1.5px dashed ${C.outline}66`,
                      background: "none", cursor: "pointer",
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center", gap: 2,
                      color: C.outline, fontFamily: FL, fontSize: 9,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>add_photo_alternate</span>
                    {t("place.add_photo")}
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                onChange={handlePhotoSelect}
                style={{ display: "none" }}
              />
              {uploadError && (
                <p style={{
                  margin: "8px 0 0", padding: "8px 12px",
                  background: "#fef0ec", borderRadius: 8,
                  fontFamily: FL, fontSize: 11, color: C.error,
                }}>{uploadError}</p>
              )}
            </Section>

            {/* Instagram link — hidden for Phase 1 */}

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={saving || photos.length === 0}
              style={{
                width: "100%", padding: "16px",
                background: (saving || photos.length === 0) ? C.outline : C.primary,
                color: "#fff6ef", border: "none", borderRadius: 12,
                fontFamily: FL, fontSize: 14, fontWeight: 700,
                cursor: (saving || photos.length === 0) ? "not-allowed" : "pointer",
                marginTop: 8,
                boxShadow: (saving || photos.length === 0) ? "none" : "0 4px 16px rgba(101,93,84,0.2)",
                letterSpacing: "0.02em",
                opacity: photos.length === 0 ? 0.5 : 1,
              }}
            >
              {photos.length === 0 ? "Add a photo to save" : uploading ? "Uploading photos..." : saving ? "Saving..." : editMode ? "Save Changes" : "Save Place"}
            </button>

            {/* Delete button (edit mode only) */}
            {editMode && onDelete && (
              <button
                onClick={() => { if (window.confirm("Delete this place?")) onDelete(); }}
                style={{
                  width: "100%", padding: "13px",
                  border: `1px solid rgba(158,66,44,0.2)`,
                  borderRadius: 10, background: "none",
                  color: C.error,
                  fontFamily: FL, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", marginTop: 8,
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(158,66,44,0.05)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "none"}
              >
                Delete Place
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <p style={{
        margin: "0 0 10px",
        fontFamily: FL,
        fontSize: 10, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.15em",
        color: "#5c605c",
      }}>{label}</p>
      {children}
    </div>
  );
}

function FolderChip({ label, color, selected, onClick, onDelete, onColorChange }) {
  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        onClick={onClick}
        style={{
          padding: "6px 12px",
          paddingRight: onDelete ? 24 : 12,
          border: `1.5px solid ${selected ? color : "rgba(175,179,174,0.4)"}`,
          borderRadius: 999,
          background: selected ? `${color}18` : "transparent",
          cursor: "pointer",
          fontFamily: FL, fontSize: 11, fontWeight: selected ? 700 : 400,
          color: selected ? color : "#777c77",
          display: "flex", alignItems: "center", gap: 5,
          transition: "all 0.15s",
        }}
      >
        {onColorChange ? (
          <label style={{ position: "relative", width: 6, height: 6, flexShrink: 0, cursor: "pointer" }}
            onClick={(e) => e.stopPropagation()}>
            <input type="color" value={color}
              onChange={(e) => onColorChange(e.target.value)}
              style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer", border: "none", padding: 0 }}
            />
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
          </label>
        ) : (
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
        )}
        {label}
      </button>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{
            position: "absolute", top: -4, right: -4,
            width: 16, height: 16, borderRadius: "50%",
            background: "rgba(47,52,48,0.5)", color: "#fff",
            border: "none", cursor: "pointer",
            fontSize: 10, lineHeight: "16px", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >&times;</button>
      )}
    </div>
  );
}
